import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { API } from "../lib/api";
import { getAlerts, markTriggered, fireBrowserNotification } from "../lib/alerts";
import { toast } from "sonner";

const DashboardContext = createContext(null);

export const useDashboard = () => useContext(DashboardContext);

export function DashboardProvider({ children }) {
  const [pairs, setPairs] = useState([]);
  const [activeSymbol, setActiveSymbol] = useState("BTCUSDT");
  const [interval, setIntervalTf] = useState("1h");
  const [providerStatus, setProviderStatus] = useState([]);
  const [settings, setSettings] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [wsConnected, setWsConnected] = useState(false);

  const loadPairs = useCallback(async () => {
    try {
      const p = await api.getPairs();
      setPairs(p);
      if (p.length && !p.find((x) => x.symbol === activeSymbol)) {
        setActiveSymbol(p[0].symbol);
      }
    } catch (e) { /* noop */ }
  }, [activeSymbol]);

  useEffect(() => { loadPairs(); }, []); // eslint-disable-line

  useEffect(() => {
    api.getSettings().then(setSettings).catch(() => {});
    const ping = () => api.getProviderStatus().then((d) => setProviderStatus(d.providers)).catch(() => {});
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, []);

  const addPair = async (symbol, color) => {
    const created = await api.addPair(symbol, color);
    await loadPairs();
    return created;
  };
  const removePair = async (id) => { await api.deletePair(id); await loadPairs(); };
  const reorder = async (order) => { const p = await api.reorderPairs(order); setPairs(p); };

  // Live price WebSocket stream (polling elsewhere remains as fallback)
  useEffect(() => {
    let ws;
    let retry;
    let stopped = false;
    const connect = async () => {
      try {
        // httpOnly cookie isn't forwarded on the WS upgrade through the proxy,
        // so authenticate the socket with a short-lived ticket.
        let ticket = "";
        try { ticket = (await api.getWsTicket()).ticket; } catch (_) { /* not authed yet */ }
        if (stopped) return;
        const wsUrl = API.replace(/^http/, "ws") + "/ws/prices" + (ticket ? `?ticket=${ticket}` : "");
        ws = new WebSocket(wsUrl);
        ws.onopen = () => setWsConnected(true);
        ws.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data);
            if (m.type === "prices" && m.data) setLivePrices(m.data);
          } catch (_) { /* ignore */ }
        };
        ws.onclose = () => {
          setWsConnected(false);
          if (!stopped) retry = setTimeout(connect, 4000);
        };
        ws.onerror = () => { try { ws.close(); } catch (_) {} };
      } catch (_) { retry = setTimeout(connect, 4000); }
    };
    connect();
    return () => { stopped = true; clearTimeout(retry); try { ws && ws.close(); } catch (_) {} };
  }, []);

  // Global alert monitor — works app-wide off the live price stream, fires
  // in-app toast AND browser notification when a price threshold is crossed.
  useEffect(() => {
    const prices = livePrices || {};
    if (!Object.keys(prices).length) return;
    const alerts = getAlerts();
    alerts.forEach((a) => {
      if (a.triggered) return;
      const lp = prices[a.symbol];
      if (!lp) return;
      const crossed = (a.cond === "above" && lp.price >= a.price) ||
                      (a.cond === "below" && lp.price <= a.price);
      if (crossed) {
        const msg = `${a.symbol} is ${a.cond} ${a.price} — now ${lp.price}`;
        toast.success(`Alert triggered: ${msg}`);
        fireBrowserNotification("YUMAERA Price Alert", msg);
        markTriggered(a.id);
      }
    });
  }, [livePrices]);

  const value = {
    pairs, activeSymbol, setActiveSymbol, interval, setIntervalTf,
    providerStatus, settings, setSettings, loadPairs, addPair, removePair, reorder,
    livePrices, wsConnected,
  };
  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}
